import {
  BaseAddress,
  Cart,
  CartDraft,
  ClientResponse,
  LineItem,
  MyCartUpdate,
  MyLineItemDraft,
  TypedMoney,
} from "@commercetools/platform-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DeliveryOption, ProductConfiguration } from "@wttb/product-configurator";
import { CustomerIntent, OrderIntent } from "@wttb/shared";
import { Session } from "next-auth";
import { getSession, signIn, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";

import { createApiClient } from "@/clients/commercetools";
import { getProductDataBySKU } from "@/clients/product-config/product";
import { updateTOSessionWithPromotions } from "@/clients/talon-one/cart";
import { CountryCode } from "@/common/constants/contry";
import { trackAddToCartEvent } from "@/features/analytics";
import { getProductConfigSummary } from "@/features/product/components/hooks/use-product-config-summary";
import { useCart } from "./use-cart";
import { updateArtworkCheckAddOn } from "./use-cart-addon";

type AddCartLineItem = {
  sku?: string;
  productId?: string;
  variantId?: number;
  quantity: number;
  config: ProductConfiguration;
  deliveryOption?: DeliveryOption | null;
  productionDays?: number;
  jobDetails?: string;
  purchaseCost?: TypedMoney;
  listPrice?: TypedMoney;
  artworkCheckPrice?: number;
};

type AddToCartPayload = {
  cartId?: string;
  cartVersion?: number;
  items: AddCartLineItem[];
  shippingAddress?: BaseAddress;
};

async function addToCart(
  session: Session | null,
  payload: AddToCartPayload,
  t: ReturnType<typeof useTranslations<string>>,
) {
  if (!session) {
    await signIn("guest", { redirect: false });
    session = await getSession();
  }

  const api = createApiClient(session!.accessToken).me();
  let customer;
  if (!session?.isAnonymous) {
    customer = await api
      .get()
      .execute()
      .then(({ body }) => body);
  }

  const cartApi = api.carts();

  const artworkCheckHash: Record<string, number> = {};

  // Create LineItems that must be added to a Cart
  const lineItems: MyLineItemDraft[] = await Promise.all(
    payload.items.map(async (item) => {
      const fields: Record<string, number | string | TypedMoney | boolean> = {
        productConfig: JSON.stringify(item.config),
      };
      if (item.deliveryOption?.deliveryRuleId) {
        fields.deliveryRuleId = item.deliveryOption.deliveryRuleId;
      }

      if ("productionDays" in item && item.productionDays !== undefined) {
        fields.productionDays = item.productionDays;
      }

      if ("listPrice" in item && item.listPrice !== undefined) {
        fields.listPrice = item.listPrice;
      }

      if ("purchaseCost" in item && item.purchaseCost !== undefined) {
        fields.purchaseCost = item.purchaseCost;
      }

      if ("jobDetails" in item && item.jobDetails !== undefined) {
        fields.jobDetails = item.jobDetails;
      }

      const lineItemKey = uuid();
      const attributes = (await getProductDataBySKU(item.sku!)).attributes;

      const productConfigDescription = getProductConfigSummary({
        config: item.config,
        attributes,
        t,
      });

      fields.productConfigDescription = JSON.stringify(productConfigDescription);

      artworkCheckHash[lineItemKey] = item.artworkCheckPrice ?? 0;

      return {
        sku: item.sku,
        key: lineItemKey,
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity || 1,
        custom: {
          type: { typeId: "type", key: "line-item-additional-fields" },
          fields,
        },
      };
    }),
  );

  let cartResponse: ClientResponse<Cart>;

  let addedLineItems: Array<LineItem>;
  if (!payload.cartId || !payload.cartVersion) {
    // Create a new Cart if not exist yet
    const body: CartDraft = {
      currency: "GBP",
      lineItems,
      customerEmail: customer?.email,
      custom: {
        type: { typeId: "type", key: "order-additional-fields" },
        fields: {
          vip: customer?.custom?.fields.vip ?? false,
          intent:
            customer?.custom?.fields.intent === CustomerIntent.Test
              ? OrderIntent.Test
              : OrderIntent.Standard,
        },
      },
      // Set default Country to trigger Tax calculation
      shippingAddress: {
        country: CountryCode.GB,
      },
    };

    cartResponse = await cartApi.post({ body }).execute();
    addedLineItems = cartResponse.body.lineItems;
  } else {
    const prevLineItems = (await cartApi.withId({ ID: payload.cartId }).get().execute()).body
      .lineItems;
    // Add LineItems to the existing Cart
    const body = {
      version: payload.cartVersion,
      actions: [
        ...lineItems.map((lineItem) => ({ ...lineItem, action: "addLineItem" })),
        // Set default country to shipping address to trigger tax calculation
        ...(!payload.shippingAddress
          ? [{ action: "setShippingAddress", address: { country: CountryCode.GB } }]
          : []),
        // We should add custom fields for existing cart again
        // because user custom field might have been changed
        {
          action: "setCustomField",
          name: "vip",
          value: customer?.custom?.fields.vip ?? false,
        },
        {
          action: "setCustomField",
          name: "intent",
          value:
            customer?.custom?.fields?.intent === CustomerIntent.Test
              ? OrderIntent.Test
              : OrderIntent.Standard,
        },
        {
          action: "setCustomerEmail",
          email: customer?.email,
        },
      ],
    } as MyCartUpdate;

    cartResponse = await cartApi.withId({ ID: payload.cartId }).post({ body }).execute();
    addedLineItems = (cartResponse.body.lineItems ?? []).reduce(
      (result, currentItem) => {
        const isInPrevLineItems = prevLineItems.some((prevItem) => prevItem.id === currentItem.id);

        if (!isInPrevLineItems) {
          result.push(currentItem);
        }

        return result;
      },
      [] as typeof cartResponse.body.lineItems,
    );
  }
  const promotionsResult = await updateTOSessionWithPromotions({ cart: cartResponse.body });
  const cart = promotionsResult?.cart ?? cartResponse.body;

  // now it's time to add custom line items when cart created and track events
  await Promise.all(
    addedLineItems.map(async (lineItem) => {
      trackAddToCartEvent({ cart, lineItem });

      const artworkCheckPrice = artworkCheckHash[lineItem.key ?? ""];
      if (artworkCheckPrice) {
        await updateArtworkCheckAddOn(cart, true, lineItem.key ?? "", artworkCheckPrice);
      }
    }),
  );

  return cart;
}

export function useAddToCartMutation() {
  const { data: cart } = useCart();

  const queryClient = useQueryClient();
  let { data: session } = useSession();
  const t = useTranslations("common");

  return useMutation(
    async (payload: AddCartLineItem | AddCartLineItem[]) => {
      if (!session) {
        await signIn("guest", { redirect: false });
        session = await getSession();
      }
      return addToCart(
        session!,
        {
          cartId: cart?.id,
          cartVersion: cart?.version,
          items: [payload].flat(),
          shippingAddress: cart?.shippingAddress,
        },
        t,
      );
    },
    {
      mutationKey: ["ADD_TO_CART"],
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["CART"] });
      },
      onError: (data: Error) => {
        toast.error("Error adding to cart: " + data.message);
      },
    },
  );
}
